/**
 * @prettier
 * @flow
 */
import * as React from 'react';
import { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { AutoCompleteInputProp, Variant } from 'src/components/form/FormTypes';
import { TransformedTextField, transformToOutput } from 'src/components/form/material-ui/TransformedTextField';
import { getError } from 'src/components/form/utils/getError';
import { translate } from 'src/utils/translate/translate';

export function FormTextField({ name, label, placeholder, helperText, defaultValue = null, trim = true, upperCase, autoComplete = 'off', multiline, rowsMax, disabled, required, minLength, maxLength, pattern, validate, variant = 'outlined', InputProps, inputProps}: Props) {

    const { errors, control, formState: { isSubmitting } } = useFormContext();
    const inputRef = useRef();

    return (
        <Controller
            render={({ value, onChange, ...props }) => (
                <TransformedTextField
                    {...props}
                    value={value}
                    onChange={onChange}
                    label={label}
                    placeholder={placeholder}
                    helperText={getError(errors, name)?.message ?? helperText}
                    trim={trim}
                    upperCase={upperCase}
                    disabled={isSubmitting || disabled}
                    required={required}
                    multiline={multiline}
                    rowsMax={rowsMax}
                    error={!!getError(errors, name)}
                    variant={variant}
                    inputRef={inputRef}
                    InputProps={InputProps}
                    inputProps={{ autoComplete, ...inputProps, style: { textTransform: upperCase ? 'uppercase' : 'none', ...inputProps?.style } }}
                    InputLabelProps={{ shrink: placeholder ? true : undefined }}
                    fullWidth
                />
            )}
            control={control}
            name={name}
            defaultValue={transformToOutput(defaultValue, trim, upperCase)}
            rules={{
                required: {
                    value: (required: any)?.value ?? required,
                    message: (required: any)?.message ??  translate('This field is required'),
                },
                minLength: {
                    value: (minLength: any)?.value ?? minLength,
                    message: (minLength: any)?.message ??  translate('Must be at least @number characters', { number: (minLength: any)?.value ?? minLength }),
                },
                maxLength: {
                    value: (maxLength: any)?.value ?? maxLength,
                    message: (maxLength: any)?.message ??  translate('Must be at most @number characters', { number: (maxLength: any)?.value ?? maxLength }),
                },
                pattern: {
                    value: (pattern: any)?.value ?? pattern,
                    message: (pattern: any)?.message ??  translate('This field is in incorrect format'),
                },
                validate,
            }}
            onFocus={() => { // make focus on error work when disabled={isSubmitting || disabled}
                if(inputRef.current) {
                    inputRef.current.disabled = false;
                    inputRef.current.focus();
                }
            }}
        />
    );
}

export type Props = {|
    name: string,
    label?: string,
    placeholder?: string,
    helperText?: string,
    defaultValue?: string,
    trim?: boolean,
    upperCase?: boolean,
    autoComplete?: AutoCompleteInputProp,
    multiline?: boolean,
    rowsMax?: number,
    disabled?: boolean,
    required?: boolean | { value: number, message: string },
    minLength?: number | { value: number, message: string },
    maxLength?: number | { value: number, message: string },
    pattern?: RegExp | { value: RegExp, message: string },
    validate?: any,
    variant?: Variant,
    InputProps?: any,
    inputProps?: any,
|};

